import { connect, ConnectedProps } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom-v5-compat';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { Team } from 'dashboard/models/team';
import { showMessage } from 'dashboard/redux/actions/messages';
import { setTeam, setTeamUserIds } from 'dashboard/redux/actions/team';
import { RootState } from 'dashboard/redux/store';
import { loadAllRoles } from 'dashboard/redux/thunk-actions/roles';
import { loadTeam, loadTeamUserIds } from 'dashboard/redux/thunk-actions/team';
import { loadUserList } from 'dashboard/redux/thunk-actions/user';
import DashboardTeamShowView from 'dashboard/views/DashboardTeamShowView';
import { FormEditTeamData } from 'dashboard/views/DashboardTeamShowView/EditTeamForm';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';

type Params = { teamId: string };

const DashboardTeamShowRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();
  const { teamId } = useParams<Params>() as Params; // teamId will always be present on this route

  const { loading: loadingTeam } = useApiLoader({
    loader: () => props.loadTeam(teamId),
    deps: [props.loadTeam, teamId],
    onError: (error) => {
      switch (error.status) {
        case 401:
          props.showMessage(
            'Your role does not have permissions to view teams in this organization.',
            MessageType.Notice,
          );
          break;
        default:
          props.showMessage('Failed to load teams.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingUsers } = useApiLoader({
    loader: () => props.loadUserList(),
    deps: [props.loadUserList],
    onError: (error) => {
      switch (error.status) {
        case 401:
          props.showMessage(
            'Your role does not have permissions to view users in this organization. You cannot add users to a team.',
            MessageType.Notice,
          );
          break;
        default:
          props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingTeamUsers } = useApiLoader({
    loader: () => props.loadTeamUserIds(teamId),
    deps: [props.loadTeamUserIds, teamId],
    onError: (error) => {
      switch (error.status) {
        case 401:
          props.showMessage(
            'Your role does not have permissions to view users in this team.',
            MessageType.Notice,
          );
          break;
        default:
          props.showMessage('Failed to load users.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { loading: loadingRoles } = useApiLoader({
    loader: () => props.loadAllRoles(),
    deps: [props.loadAllRoles],
    onError: (error) => {
      switch (error.status) {
        case 401:
          props.showMessage(
            'Your role does not have permissions to view roles in this organization.',
            MessageType.Notice,
          );
          break;
        default:
          props.showMessage('Failed to load roles.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: updateTeamError, callback: updateTeam } = useApiCallback(
    async ({ name, 'user-ids': userIds }: FormEditTeamData) => {
      const { data: team } = await SessionClientDashboard.updateResource<
        Team,
        {
          type: ApiModel.Team;
          id: string;
          attributes: { name: string };
        }
      >({
        type: ApiModel.Team,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        id: props.team.id,
        attributes: { name },
      });
      const teamUsersResponse = await SessionClientDashboard.postResourceAction(
        {
          type: ApiModel.Team,
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          id: props.team.id,
        },
        'update-team-users',
        {
          meta: {
            'user-ids': userIds,
          },
        },
      );
      props.setTeam(team);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      props.setTeamUserIds(teamUsersResponse.meta['team-user-ids']);
      props.showMessage('Team was successfully updated', MessageType.Success);
    },
    [props.showMessage, props.setTeam, props.setTeamUserIds, props.team],
  );

  useApiErrorHandler({
    error: updateTeamError,
    onError: () =>
      props.showMessage('Failed to update team, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to update team, please try again.', MessageType.Failure),
  });

  const { error: deleteTeamError, callback: deleteTeam } = useApiCallback(
    async (teamId: string) => {
      const deleteTeamResponse = await SessionClientDashboard.deleteResource<Team>(
        ApiEndpoint.Team,
        teamId,
      );
      props.setTeam(deleteTeamResponse.data);
      props.showMessage('Successfully deleted team.', MessageType.Success);
      navigate(`/dashboard/teams`);
    },
    [props.showMessage, props.setTeam, navigate],
  );

  useApiErrorHandler({
    error: deleteTeamError,
    onError: () =>
      props.showMessage('Failed to deleted team, please try again.', MessageType.Failure),
    onNetworkError: () =>
      props.showMessage('Failed to deleted team, please try again.', MessageType.Failure),
  });

  return (
    <DashboardTeamShowView
      isloading={loadingTeam || loadingTeamUsers || loadingRoles || loadingUsers}
      allRoles={props.allRoles}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      team={props.team}
      teamUserIds={props.teamUserIds}
      users={props.users}
      deleteTeam={deleteTeam}
      updateTeam={updateTeam}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    allRoles: state.roles.allRoles,
    team: state.team.team,
    teamUserIds: state.team.teamUserIds,
    users: state.user.users,
  };
};

const mapDispatchToProps = {
  loadTeam,
  loadTeamUserIds,
  loadAllRoles,
  setTeam,
  setTeamUserIds,
  loadUserList,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardTeamShowRoute);
